import { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';

// Components
import { PdpRespIconBubblePrint } from './PdpRespIconBubblePrint';
import { PdpRespIconBubbleDesignManager } from './PdpRespIconBubbleDesignManager';

import { PdpRespIconBubbles } from './PdpRespIconBubbles';

import {
    DESIGN_MANAGER_PRO,
    DESIGN_MANAGER_STANDARD,
} from 'dibs-trade-design-manager/exports/tradeDesignManagerAuthHelpers';

import { PdpRespIconBubblesWrapper_viewer$data } from './__generated__/PdpRespIconBubblesWrapper_viewer.graphql';
import { PdpRespIconBubblesWrapper_item$data } from './__generated__/PdpRespIconBubblesWrapper_item.graphql';
import { PdpRespIconBubblesWrapper_user$data } from './__generated__/PdpRespIconBubblesWrapper_user.graphql';

import styles from './PdpRespIconBubblesWrapper.scss';

type Props = {
    relayVariables: {
        isClient?: boolean;
        isTrade?: boolean;
    };
    viewer: PdpRespIconBubblesWrapper_viewer$data;
    item: PdpRespIconBubblesWrapper_item$data;
    user: PdpRespIconBubblesWrapper_user$data | null;
    skuId: string;
    dataTn?: string;
};

const getIsDMFeatEligible = (user: PdpRespIconBubblesWrapper_user$data | null): boolean => {
    const { hasAccount, accountType } = user?.tradeFirmDesignManagerProfile || {};
    const isDMFeatEligible =
        hasAccount &&
        (accountType === DESIGN_MANAGER_PRO || accountType === DESIGN_MANAGER_STANDARD);
    return !!isDMFeatEligible;
};

export const PdpRespIconBubblesWrapperComponent: FC<Props> = ({
    dataTn,
    relayVariables,
    ...props
}) => {
    const isPrivatePdp = props.item.isPublishedPrivate;
    if (isPrivatePdp) {
        return null;
    }

    const { isTrade, isClient } = relayVariables;
    const isDMFeatEligible = isTrade && getIsDMFeatEligible(props.user);

    return (
        <div className={styles.wrapper} data-tn={dataTn}>
            {isDMFeatEligible && <PdpRespIconBubbleDesignManager {...props} />}
            <PdpRespIconBubbles {...props} isClient={isClient} />
            <PdpRespIconBubblePrint isTrade={isTrade} />
        </div>
    );
};

export const PdpRespIconBubblesWrapper = createFragmentContainer(
    PdpRespIconBubblesWrapperComponent,
    {
        viewer: graphql`
            fragment PdpRespIconBubblesWrapper_viewer on Viewer {
                ...PdpRespIconBubbles_viewer
            }
        `,
        item: graphql`
            fragment PdpRespIconBubblesWrapper_item on Item {
                isPublishedPrivate
                ...PdpRespIconBubbles_item
                ...PdpRespIconBubbleDesignManager_item @include(if: $isTrade)
            }
        `,
        user: graphql`
            fragment PdpRespIconBubblesWrapper_user on User {
                tradeFirmDesignManagerProfile @include(if: $isTrade) {
                    hasAccount
                    accountType
                }
                ...PdpRespIconBubbleDesignManager_user @include(if: $isTrade)
                ...PdpRespIconBubbles_user
            }
        `,
    }
);
